import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useToast from "~/components/ui/toast";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { invokeQuickAction, mutateQuickActionsAvailable } from "~/services/quickActions";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";

/**
 * Invokes a quick action and navigates to the project it materializes.
 * Session-starting (phase 2) will extend the success path once the backend
 * populates `session` instead of always returning null.
 */
export const useInvokeQuickAction = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [invokingId, setInvokingId] = useState<string | null>(null);

  const invoke = async (quickActionId: string) => {
    setInvokingId(quickActionId);
    try {
      const response = await invokeQuickAction(quickActionId);
      navigate(nextgenProjectPath(response.project.id));
    } catch (error) {
      if (isApiError(error) && error.status === 403) {
        showError("You don't have access to a dataset or agent this action needs.");
      } else if (isApiError(error) && error.status === 404) {
        showError("This quick action is no longer available.");
        void mutateQuickActionsAvailable();
      } else if (isApiError(error)) {
        showError(getApiErrorMessage(error));
      } else {
        showError("Something went wrong running that action.");
      }
    } finally {
      setInvokingId(null);
    }
  };

  return { invoke, invokingId };
};
